import { supabase } from '../supabase';

// Configurazione per fetch dirette (usiamo REST API invece del client per migliori performance)
const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;

console.log('💾 [DATABASE] Core module loaded - usando fetch dirette REST API');

// Errore usato quando non c'è una sessione utente valida: NON degradiamo mai
// alla anon key per le query dati (con RLS restituirebbe 0 righe → "profilo
// vuoto" pur avendo i dati). Meglio fallire in modo esplicito e ritentare.
export class SessionExpiredError extends Error {
  constructor() {
    super('SESSION_EXPIRED');
    this.name = 'SessionExpiredError';
    this.isSessionExpired = true;
  }
}

// Notifica l'app (SessionExpiredHandler) che la sessione non è recuperabile:
// throttle perché più fetch parallele falliscono insieme (es. dashboard).
let lastSessionExpiredEventAt = 0;
const emitSessionExpired = () => {
  const now = Date.now();
  if (now - lastSessionExpiredEventAt < 5000) return;
  lastSessionExpiredEventAt = now;
  try {
    window.dispatchEvent(new CustomEvent('uptra:session-expired'));
  } catch (e) { /* ambienti senza window/CustomEvent */ }
};

// Distingue il fallimento DEFINITIVO (refresh rifiutato dal server: la
// sessione non tornerà) dal fallimento TRANSITORIO (timeout per rete lenta o
// contesa del lock auth fra tab): solo il primo deve portare al login.
let lastAuthFailureDefinitive = false;
export const wasLastAuthFailureDefinitive = () => lastAuthFailureDefinitive;

// Token della sessione con timeout + un tentativo di refresh. Ritorna null se
// non c'è una sessione valida (MAI la anon key).
export const getAuthToken = async () => {
  const withTimeout = (p, ms, label) =>
    Promise.race([p, new Promise((_, r) => setTimeout(() => r(new Error(label)), ms))]);

  lastAuthFailureDefinitive = false;

  try {
    const { data: { session }, error } = await withTimeout(
      supabase.auth.getSession(), 5000, 'getSession timeout'
    );
    if (!error && session?.access_token) return session.access_token;
  } catch (err) {
    console.warn('💾 [DATABASE] getSession non disponibile:', err.message);
  }

  // Nessun token: prova un refresh esplicito prima di arrenderti
  try {
    const { data, error } = await withTimeout(
      supabase.auth.refreshSession(), 8000, 'refreshSession timeout'
    );
    if (!error && data?.session?.access_token) {
      console.log('💾 [DATABASE] Sessione ripristinata via refresh');
      return data.session.access_token;
    }
    // Il server ha RISPOSTO (niente timeout) rifiutando il refresh:
    // sessione davvero scaduta/revocata.
    if (error) lastAuthFailureDefinitive = true;
  } catch (err) {
    console.warn('💾 [DATABASE] refreshSession fallito:', err.message);
    // Timeout/errore di rete: transitorio, NON dichiarare la sessione scaduta
  }

  return null; // → supabaseFetch lancerà SessionExpiredError
};

// Helper per fetch con headers Supabase (con retry automatico su 401 e errori di rete)
// providedToken: se già disponibile (es. da onAuthStateChange) evita di chiamare getSession() di nuovo
export const supabaseFetch = async (endpoint, options = {}, _retryCount = 0, providedToken = null) => {
  const MAX_RETRIES = 3;
  const TIMEOUT_MS = 30000; // 30 secondi timeout

  console.log('💾 [DATABASE] supabaseFetch called for:', endpoint, _retryCount > 0 ? `(retry ${_retryCount})` : '');
  const token = providedToken || await getAuthToken();

  // Nessuna sessione valida: NON interroghiamo con la anon key (RLS → 0 righe
  // = falso "profilo vuoto"). Falliamo esplicito: l'app può ritentare/riportare
  // al login invece di mostrare dati vuoti o dati anonimi.
  if (!token) {
    // Al login si va SOLO se la sessione è irrecuperabile per davvero
    // (refresh rifiutato dal server); i timeout transitori restano errori
    // locali con retry, come prima.
    if (wasLastAuthFailureDefinitive()) {
      emitSessionExpired();
    }
    throw new SessionExpiredError();
  }

  // Create AbortController for timeout
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), TIMEOUT_MS);

  try {
    const response = await fetch(`${SUPABASE_URL}/rest/v1${endpoint}`, {
      ...options,
      signal: controller.signal,
      headers: {
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'Prefer': 'return=representation',
        ...options.headers,
      },
    });

    clearTimeout(timeoutId);
    console.log('💾 [DATABASE] Fetch response status:', response.status);

    // On 401/403: try refreshing session once, then retry
    if ((response.status === 401 || response.status === 403) && _retryCount === 0) {
      console.warn('💾 [DATABASE] Got', response.status, '- attempting session refresh...');
      try {
        const { data, error } = await supabase.auth.refreshSession();
        if (!error && data?.session) {
          console.log('💾 [DATABASE] Session refreshed, retrying request...');
          return supabaseFetch(endpoint, options, 1, data?.session?.access_token || null);
        }
      } catch (e) {
        console.warn('💾 [DATABASE] Session refresh failed:', e.message);
      }
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      console.error('💾 [DATABASE] Fetch error:', error);
      throw new Error(error.message || `HTTP ${response.status}`);
    }

    // Alcune risposte non hanno body (es. 204 No Content da RPC che ritornano void):
    // non forzare response.json() su un body vuoto (causerebbe "Unexpected end of JSON input").
    if (response.status === 204) {
      return null;
    }
    const text = await response.text();
    return text ? JSON.parse(text) : null;
  } catch (err) {
    clearTimeout(timeoutId);

    // Check if it's a network error or timeout that we should retry
    const isNetworkError = err.name === 'AbortError' ||
                          err.name === 'TypeError' ||
                          err.message.includes('network') ||
                          err.message.includes('fetch') ||
                          err.message.includes('Failed to fetch');

    if (isNetworkError && _retryCount < MAX_RETRIES) {
      const delay = Math.min(1000 * Math.pow(2, _retryCount), 5000); // Exponential backoff, max 5s
      console.warn(`💾 [DATABASE] Network error, retrying in ${delay}ms... (attempt ${_retryCount + 1}/${MAX_RETRIES})`);
      await new Promise(resolve => setTimeout(resolve, delay));
      return supabaseFetch(endpoint, options, _retryCount + 1, providedToken);
    }

    // If it's a timeout, provide a clearer error message
    if (err.name === 'AbortError') {
      throw new Error('Connessione lenta - riprova');
    }

    throw err;
  }
};
